import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import { useTitleStore } from 'src/stores/useTitleStore';
import * as React from 'react';
import { Button } from 'src/registry/ui/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { booleanBadgeOptions, ICON_MAP } from 'src/utils/constants';
import { SectionBlock, SectionBlockHeader, SectionBlockTitle } from 'src/registry/components/blocks/SectionBlock';
import { commonMessages, generalMessages } from 'src/utils/messages';
import DashboardGeneralBlock from 'src/features/billing/components/DashboardGeneralBlock';
import { CellCurrencyBadge } from 'src/features/moneyAccounts/components/tables/CellCurrencyBadge';
import { getFormattedDateTimeForDetailPanel } from 'src/utils/getFormattedDateTime';
import { discountPoliciesMessages } from 'src/features/discountPolicies/utils/messages';
import { pricingPoliciesMessages } from 'src/features/servicePlanPricingPolicies/utils/messages';
import { voucherTypesMessages } from 'src/features/voucherTypes/utils/messages';
import { Badge } from 'src/registry/ui/Badge';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { VOUCHER_USAGE_BADGE_OPTIONS } from 'src/features/voucherTypes/utils/constants';
import { vouchersMessages } from 'src/features/vouchers/utils/messages';
import { useGetVoucher } from 'src/features/vouchers/api/useGetVoucher';
import UpdateVoucherFormModal from 'src/features/vouchers/components/forms/UpdateVoucherFormModal';
import DeleteVoucherFormModal from 'src/features/vouchers/components/forms/DeleteVoucherFormModal';
import { customersMessages } from 'src/features/customers/utils/messages';
import ChargesTable from 'src/features/charges/components/tables/ChargesTable';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';
import { LinkWithIcon } from 'src/registry/components/navigation/LinkWithIcon';

const VoucherDashboard = () => {
    const { formatMessage, formatNumber } = useIntl();
    const navigate = useNavigate();
    const { voucherId = '' } = useParams();

    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(vouchersMessages.voucherDashboard));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const { data: voucher } = useGetVoucher({
        voucherId,
    });

    const permissionManageVouchers = useHasPermissions([PERMISSION.MANAGE_VOUCHERS]);
    const permissionAccessCharges = useHasPermissions([PERMISSION.ACCESS_CHARGES]);
    const permissionAccessCustomers = useHasPermissions([PERMISSION.ACCESS_CUSTOMERS]);

    const isUsedAsString = voucher?.is_used ? 'true' : 'false';

    const usedAmountGreaterThanZero = voucher?.used_amount ? voucher?.used_amount > 0 : false;
    const isVoucherUsed = voucher?.is_used;

    const dashboardBlockData = {
        section1: [
            {
                title: voucherTypesMessages.code,
                value: voucher?.code ?? '-',
            },
            {
                title: vouchersMessages.issuedAt,
                value: getFormattedDateTimeForDetailPanel(voucher?.issued_at) ?? '-',
            },
            {
                title: vouchersMessages.expiresAt,
                value: voucher?.expires_at ? getFormattedDateTimeForDetailPanel(voucher?.expires_at) : '-',
            },
            {
                title: vouchersMessages.issuedBy,
                value: voucher?.issued_by?.name ?? '-',
            },
        ],
        section2: [
            {
                title: voucherTypesMessages.voucherType,
                value: voucher?.voucher_type && (
                    <LinkWithIcon
                        to={`/voucher-types/${voucher?.voucher_type.id}`}
                        linkTitle={voucher?.voucher_type_name}
                        hasPermissions={true}
                    />
                ),
            },
            {
                title: customersMessages.customer,
                value: voucher?.customer && (
                    <LinkWithIcon
                        to={`/customers/${voucher?.customer.id}`}
                        linkTitle={voucher?.customer.name}
                        hasPermissions={permissionAccessCustomers}
                    />
                ),
            },
            {
                title: voucherTypesMessages.usage,
                value: voucher?.usage ? (
                    <Badge theme={VOUCHER_USAGE_BADGE_OPTIONS[voucher.usage].theme || 'gray'}>
                        <FontAwesomeIcon icon={VOUCHER_USAGE_BADGE_OPTIONS[voucher.usage].icon as IconProp} />
                        {formatMessage(VOUCHER_USAGE_BADGE_OPTIONS[voucher.usage].label)}
                    </Badge>
                ) : (
                    '-'
                ),
            },
            {
                title: vouchersMessages.isUsed,
                value: (
                    <Badge theme={booleanBadgeOptions[isUsedAsString].theme || 'gray'}>
                        <FontAwesomeIcon icon={booleanBadgeOptions[isUsedAsString].icon as IconProp} />
                        {formatMessage(booleanBadgeOptions[isUsedAsString].label)}
                    </Badge>
                ),
            },
        ],
        section3: [
            {
                title: commonMessages.currency,
                value: voucher?.currency ? <CellCurrencyBadge currency={voucher?.currency} /> : '-',
            },
            {
                title: pricingPoliciesMessages.amount,
                value:
                    voucher?.amount && voucher.currency
                        ? formatNumber(voucher?.amount / 100, {
                              style: 'currency',
                              currency: voucher.currency,
                          })
                        : '-',
            },
            {
                title: vouchersMessages.usedAmount,
                value:
                    voucher?.used_amount && voucher.currency
                        ? formatNumber(voucher?.used_amount / 100, {
                              style: 'currency',
                              currency: voucher.currency,
                          })
                        : '-',
            },
            {
                title: vouchersMessages.remainingAmount,
                value:
                    voucher?.remaining_amount && voucher.currency
                        ? formatNumber(voucher?.remaining_amount / 100, {
                              style: 'currency',
                              currency: voucher.currency,
                          })
                        : '-',
            },
            {
                title: discountPoliciesMessages.percent,
                value: voucher?.percent ? `${(Number(voucher.percent) * 100).toFixed(2)}%` : '-',
            },
        ],
    };

    return (
        <>
            <Button asChild className="place-self-start p-0" variant="link" onClick={() => navigate('/vouchers')}>
                <div className="flex cursor-pointer">
                    <FontAwesomeIcon icon={ICON_MAP.BACK} className="opacity-50" />
                    {formatMessage(vouchersMessages.backToAllVouchers)}
                </div>
            </Button>

            <div className="mt-6 grid grid-cols-1 gap-8">
                <SectionBlock>
                    <SectionBlockHeader>
                        <SectionBlockTitle className="text-2xl whitespace-nowrap">
                            {`${voucher?.voucher_type_name} - ${voucher?.code}`}
                        </SectionBlockTitle>
                        {permissionManageVouchers && !isVoucherUsed && !usedAmountGreaterThanZero && voucher && (
                            <div className="flex flex-row gap-2">
                                <UpdateVoucherFormModal
                                    semantics={{
                                        entryName: formatMessage(voucherTypesMessages.voucher).toLowerCase(),
                                    }}
                                    selectedVoucher={voucher}
                                >
                                    <Button size="mdIcon" variant="outline" className="cursor-pointer">
                                        <FontAwesomeIcon icon={ICON_MAP.EDIT} />
                                        <span className="hidden md:block">{formatMessage(generalMessages.edit)}</span>
                                    </Button>
                                </UpdateVoucherFormModal>
                                <DeleteVoucherFormModal
                                    selectedVoucher={voucher}
                                    semantics={{
                                        entryName: formatMessage(voucherTypesMessages.voucher).toLowerCase(),
                                    }}
                                >
                                    <Button size="mdIcon" variant="destructiveLight" className="cursor-pointer">
                                        <FontAwesomeIcon icon={ICON_MAP.DELETE} />
                                        <span className="hidden md:block">{formatMessage(generalMessages.delete)}</span>
                                    </Button>
                                </DeleteVoucherFormModal>
                            </div>
                        )}
                    </SectionBlockHeader>
                    <DashboardGeneralBlock dashboardData={dashboardBlockData} indexForLastSeparator={2} />
                </SectionBlock>
                {/* A single table needs no tab row — a one-entry row is a control that cannot act. */}
                {permissionAccessCharges && (
                    <div className="grid flex-3 grid-cols-1">
                        <ChargesTable />
                    </div>
                )}
            </div>
        </>
    );
};

export default VoucherDashboard;
